import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, CircularProgress, Alert, Chip,
  Checkbox, FormControl, Select, MenuItem, TextField, InputAdornment,
} from "@mui/material";
import { useAuth } from "./AuthContext";

const CAMPAIGNS_URL = process.env.REACT_APP_CAMPAIGNS_URL || "";
const PHP_API_URL   = process.env.REACT_APP_PHP_API_URL   || "";

const C = {
  navy:   "#0B1F3A",
  red:    "#FF0A4B",
  white:  "#FFFFFF",
  bg:     "#F5F7FA",
  muted:  "#8899aa",
  border: "#dde3ec",
};

const PARTY_COLOR  = { REP: "#c0392b", DEM: "#2563eb", IND: "#6b7280" };
const PARTY_OPTIONS = [
  { value: "REP", label: "Republican" },
  { value: "DEM", label: "Democrat" },
  { value: "IND", label: "Independent" },
];
const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

// ─── Shared helpers ───────────────────────────────────────────────────────────

const btnPrimary = {
  bgcolor: C.navy, color: C.white, fontWeight: 700, textTransform: "none",
  borderRadius: 1, py: 1.1,
  "&:hover": { bgcolor: "#162d52" },
  "&:disabled": { bgcolor: C.muted },
};

const selectSx = { fontSize: 13, borderRadius: 1 };

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1, fontSize: 13,
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.navy },
    "&.Mui-focused fieldset": { borderColor: C.navy },
  },
};

function FieldRow({ label, children }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5 }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function BackBtn({ onClick, label = "← Back" }) {
  return (
    <Button size="small" onClick={onClick}
      sx={{ mb: 2, textTransform: "none", color: C.muted, fontSize: 12, pl: 0, minWidth: 0 }}>
      {label}
    </Button>
  );
}

// Normalize FEC "LAST, FIRST MIDDLE" → "First Last" or plain "First Last" → title case
function fmtName(raw) {
  if (!raw) return "";
  const s = (raw || "").trim();
  if (s.includes(",")) {
    const [last, rest] = s.split(/,\s*/);
    const first = (rest || "").trim().split(/\s+/)[0];
    return `${toTc(first)} ${toTc(last)}`;
  }
  return s.split(/\s+/).map(toTc).join(" ");
}

function toTc(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";
}

function PartyChip({ party }) {
  const p = (party || "").substring(0, 3).toUpperCase();
  return (
    <Chip
      label={p || "—"} size="small"
      sx={{ bgcolor: PARTY_COLOR[p] || C.muted, color: "#fff", fontWeight: 700, fontSize: 10, height: 18, flexShrink: 0 }}
    />
  );
}

// ─── Step 0: Level selection ──────────────────────────────────────────────────

function LevelPanel({ onSelect }) {
  const levels = [
    { id: "federal", label: "Federal",  desc: "House, Senate, President — FEC data" },
    { id: "state",   label: "State",    desc: "Governor, AG, Legislature — TransparencyUSA data" },
    { id: "local",   label: "Local",    desc: "Mayor, County, School Board — manual entry" },
  ];
  return (
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: C.navy, mb: 0.5 }}>
        What type of race?
      </Typography>
      <Typography sx={{ fontSize: 12, color: C.muted, mb: 2.5 }}>
        We'll fetch the candidate field from the appropriate data source.
      </Typography>
      {levels.map(l => (
        <Box
          key={l.id} onClick={() => onSelect(l.id)}
          sx={{
            border: `1.5px solid ${C.border}`, borderRadius: 1.5, p: 2, mb: 1.5,
            cursor: "pointer", bgcolor: C.white,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            "&:hover": { borderColor: C.navy, bgcolor: "#f0f4fa" }, transition: "all 0.12s",
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: C.navy }}>{l.label}</Typography>
            <Typography sx={{ fontSize: 12, color: C.muted }}>{l.desc}</Typography>
          </Box>
          <Typography sx={{ color: C.muted, fontSize: 20, lineHeight: 1 }}>›</Typography>
        </Box>
      ))}
    </Box>
  );
}

// ─── Step 1: Filter (Federal / State) ────────────────────────────────────────

function FilterPanel({ level, onSearch, loading, error }) {
  const defaultCycle = String(new Date().getFullYear() + (new Date().getMonth() >= 9 ? 1 : 0));
  // Local draft state — per arch tenet 13
  const [state,  setState]  = useState("SC");
  const [cycle,  setCycle]  = useState(defaultCycle);
  const [office, setOffice] = useState("H");

  return (
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: C.navy, mb: 0.5 }}>
        {level === "federal" ? "Federal races" : "State races"}
      </Typography>
      <Typography sx={{ fontSize: 12, color: C.muted, mb: 2 }}>
        {level === "federal"
          ? "Fetches candidates from the FEC database and groups them by race."
          : "Fetches races from TransparencyUSA for the selected state and cycle."}
      </Typography>

      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <FieldRow label="State">
            <FormControl size="small" fullWidth>
              <Select value={state} onChange={e => setState(e.target.value)} sx={selectSx} MenuProps={{ disablePortal: true, disableScrollLock: true }}>
                {US_STATES.map(s => <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </FieldRow>
        </Box>
        <Box sx={{ flex: 1 }}>
          <FieldRow label="Cycle">
            <FormControl size="small" fullWidth>
              <Select value={cycle} onChange={e => setCycle(e.target.value)} sx={selectSx} MenuProps={{ disablePortal: true, disableScrollLock: true }}>
                {["2024","2025","2026","2027","2028"].map(y =>
                  <MenuItem key={y} value={y} sx={{ fontSize: 13 }}>{y}</MenuItem>)}
              </Select>
            </FormControl>
          </FieldRow>
        </Box>
      </Box>

      {level === "federal" && (
        <FieldRow label="Office type">
          <FormControl size="small" fullWidth>
            <Select value={office} onChange={e => setOffice(e.target.value)} sx={selectSx} MenuProps={{ disablePortal: true, disableScrollLock: true }}>
              <MenuItem value="H" sx={{ fontSize: 13 }}>U.S. House</MenuItem>
              <MenuItem value="S" sx={{ fontSize: 13 }}>U.S. Senate</MenuItem>
            </Select>
          </FormControl>
        </FieldRow>
      )}

      {error && <Alert severity="error" sx={{ mb: 2, fontSize: 12 }}>{error}</Alert>}

      <Button
        fullWidth variant="contained" disabled={loading}
        onClick={() => onSearch({ state, cycle: parseInt(cycle), office })}
        sx={btnPrimary}
      >
        {loading ? <CircularProgress size={18} sx={{ color: C.white }} /> : "Find races"}
      </Button>
    </Box>
  );
}

// ─── Step 1 (Local): Manual candidate entry ───────────────────────────────────

function LocalPanel({ onSubmit, onBack }) {
  const defaultCycle = String(new Date().getFullYear() + (new Date().getMonth() >= 9 ? 1 : 0));
  const [firstName, setFirstName] = useState("");
  const [lastName,  setLastName]  = useState("");
  const [party,     setParty]     = useState("REP");
  const [state,     setState]     = useState("SC");
  const [office,    setOffice]    = useState("");
  const [cycle,     setCycle]     = useState(defaultCycle);
  const [err,       setErr]       = useState("");

  const submit = () => {
    if (!firstName.trim() || !lastName.trim()) { setErr("First and last name are required."); return; }
    if (!office.trim())                         { setErr("Office is required."); return; }
    const name = `${firstName.trim()} ${lastName.trim()}`;
    onSubmit(
      { name, party, state, office_full: office.trim() },
      { state, cycle: parseInt(cycle) || new Date().getFullYear(), office: "" }
    );
  };

  return (
    <Box>
      <BackBtn onClick={onBack} />
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: C.navy, mb: 0.5 }}>Local race</Typography>
      <Typography sx={{ fontSize: 12, color: C.muted, mb: 2 }}>
        Enter the candidate's details manually. Additional settings can be configured in the campaign drawer.
      </Typography>

      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <FieldRow label="First name">
            <TextField size="small" fullWidth value={firstName}
              onChange={e => setFirstName(e.target.value)} sx={inputSx} />
          </FieldRow>
        </Box>
        <Box sx={{ flex: 1 }}>
          <FieldRow label="Last name">
            <TextField size="small" fullWidth value={lastName}
              onChange={e => setLastName(e.target.value)} sx={inputSx} />
          </FieldRow>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <FieldRow label="Party">
            <FormControl size="small" fullWidth>
              <Select value={party} onChange={e => setParty(e.target.value)} sx={selectSx} MenuProps={{ disablePortal: true, disableScrollLock: true }}>
                {PARTY_OPTIONS.map(o =>
                  <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
          </FieldRow>
        </Box>
        <Box sx={{ flex: 1 }}>
          <FieldRow label="State">
            <FormControl size="small" fullWidth>
              <Select value={state} onChange={e => setState(e.target.value)} sx={selectSx} MenuProps={{ disablePortal: true, disableScrollLock: true }}>
                {US_STATES.map(s => <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </FieldRow>
        </Box>
      </Box>

      <FieldRow label="Office">
        <TextField size="small" fullWidth value={office}
          onChange={e => setOffice(e.target.value)}
          placeholder="e.g. Mayor, County Commissioner, School Board"
          sx={inputSx} />
      </FieldRow>

      <FieldRow label="Election cycle">
        <FormControl size="small" fullWidth>
          <Select value={cycle} onChange={e => setCycle(e.target.value)} sx={selectSx} MenuProps={{ disablePortal: true, disableScrollLock: true }}>
            {["2024","2025","2026","2027","2028"].map(y =>
              <MenuItem key={y} value={y} sx={{ fontSize: 13 }}>{y}</MenuItem>)}
          </Select>
        </FormControl>
      </FieldRow>

      {err && <Alert severity="error" sx={{ mb: 2, fontSize: 12 }}>{err}</Alert>}

      <Button fullWidth variant="contained" onClick={submit} sx={btnPrimary}>
        Continue
      </Button>
    </Box>
  );
}

// ─── Step 2: Race list ────────────────────────────────────────────────────────

function RaceListPanel({ races, onSelectRace, onBack }) {
  if (!races?.length) {
    return (
      <Box>
        <BackBtn onClick={onBack} label="← Change filters" />
        <Typography sx={{ fontSize: 13, color: C.muted, textAlign: "center", py: 3 }}>
          No races found — try different filters.
        </Typography>
        <Button fullWidth onClick={onBack} sx={{ ...btnPrimary, mt: 1 }}>Change filters</Button>
      </Box>
    );
  }
  return (
    <Box>
      <BackBtn onClick={onBack} label="← Change filters" />
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: C.navy, mb: 0.5 }}>Select a race</Typography>
      <Typography sx={{ fontSize: 12, color: C.muted, mb: 2 }}>
        {races.length} race{races.length !== 1 ? "s" : ""} found
      </Typography>
      <Box sx={{ maxHeight: 380, overflowY: "auto", pr: 0.5 }}>
        {races.map((race, i) => {
          const cnt = race.candidates?.length ?? race.candidate_count;
          return (
            <Box
              key={race.race_id ?? i}
              onClick={() => onSelectRace(race)}
              sx={{
                border: `1px solid ${C.border}`, borderRadius: 1.5, p: 2, mb: 1,
                cursor: "pointer", bgcolor: C.white,
                display: "flex", alignItems: "center", justifyContent: "space-between",
                "&:hover": { borderColor: C.navy, bgcolor: "#f0f4fa" }, transition: "all 0.12s",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: C.navy }}>
                  {race.office_full}
                  {race.district && race.district !== "00" ? ` · District ${race.district}` : ""}
                </Typography>
                {cnt != null && (
                  <Typography sx={{ fontSize: 12, color: C.muted, mt: 0.25 }}>
                    {cnt} candidate{cnt !== 1 ? "s" : ""}
                  </Typography>
                )}
              </Box>
              <Typography sx={{ color: C.muted, fontSize: 20, lineHeight: 1 }}>›</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ─── Step 3: Candidate selection ──────────────────────────────────────────────

function CandidatePanel({ race, candidates, loading, error, onSelectPrimary, onBack }) {
  // Manual add — for a real candidate not yet filed with the FEC (e.g. a declared-but-unfiled
  // Senate run). Attaches to THIS federal race as primary; the FEC field becomes the opponents.
  const [adding, setAdding] = useState(false);
  const [mFirst, setMFirst] = useState("");
  const [mLast,  setMLast]  = useState("");
  const [mParty, setMParty] = useState("REP");
  const [mErr,   setMErr]   = useState("");

  const addManual = () => {
    if (!mFirst.trim() || !mLast.trim()) { setMErr("First and last name are required."); return; }
    const cand = {
      fec_id:      null,
      name:        `${mFirst.trim()} ${mLast.trim()}`,
      party:       mParty,
      office:      race?.office ?? "",
      office_full: race?.office_full ?? "",
      state:       race?.state ?? "",
      district:    race?.district ?? "",
      manual:      true,
    };
    // Set as primary; the FEC candidates already loaded become the challenger field.
    onSelectPrimary(cand, candidates);
  };

  const fmtMoney = (n) => {
    if (!n) return null;
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M raised`;
    if (n >= 1_000)     return `$${(n / 1_000).toFixed(0)}K raised`;
    return `$${n.toLocaleString()} raised`;
  };

  return (
    <Box>
      <BackBtn onClick={onBack} label="← Back to races" />
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: C.navy, mb: 0.25 }}>
        Select your candidate
      </Typography>
      <Typography sx={{ fontSize: 12, color: C.muted, mb: 2 }}>
        {race?.office_full} · {race?.state} · {race?.cycle} — campaigns will be created for all candidates shown.
      </Typography>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 2, fontSize: 12 }}>{error}</Alert>}
      {!loading && !error && candidates.length === 0 && (
        <Typography sx={{ fontSize: 13, color: C.muted, textAlign: "center", py: 2 }}>
          No candidates found for this race.
        </Typography>
      )}

      <Box sx={{ maxHeight: 360, overflowY: "auto", pr: 0.5 }}>
        {candidates.map((c, i) => (
          <Box
            key={c.fec_id ?? c.transparency_id ?? c.transparency_slug ?? i}
            sx={{
              border: `1px solid ${C.border}`, borderRadius: 1.5, p: 1.5, mb: 1, bgcolor: C.white,
              display: "flex", alignItems: "center", gap: 1.5,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, opacity: (c.status === "Withdrew" || c.status === "Disqualified") ? 0.5 : 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: C.navy }}>
                {fmtName(c.name)}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25, flexWrap: "wrap" }}>
                {c.party && <PartyChip party={c.party} />}
                {c.status && c.status !== "On the Ballot" && (
                  <Typography sx={{ fontSize: 11, color: C.red, fontWeight: 600 }}>{c.status}</Typography>
                )}
                {c.election_raised
                  ? <Typography sx={{ fontSize: 11, color: C.muted }}>{fmtMoney(c.election_raised)}</Typography>
                  : c.principal_committees?.[0]
                    ? <Typography sx={{ fontSize: 11, color: C.muted }}>{c.principal_committees[0].name}</Typography>
                    : null}
                {c.incumbent_challenge && (
                  <Typography sx={{ fontSize: 11, color: C.muted }}>{c.incumbent_challenge}</Typography>
                )}
              </Box>
            </Box>
            <Button
              size="small" variant="outlined"
              onClick={() => onSelectPrimary(c, candidates)}
              sx={{
                fontSize: 11, textTransform: "none", fontWeight: 600,
                borderColor: C.navy, color: C.navy, borderRadius: 1, py: 0.5, px: 1.5, flexShrink: 0,
                "&:hover": { bgcolor: C.navy, color: C.white, borderColor: C.navy },
              }}
            >
              Set as primary
            </Button>
          </Box>
        ))}
      </Box>

      {!loading && (
        !adding ? (
          <Button
            size="small" onClick={() => { setAdding(true); setMErr(""); }}
            sx={{ mt: 1, textTransform: "none", color: C.navy, fontSize: 12, fontWeight: 700, pl: 0, minWidth: 0 }}>
            ＋ Add a candidate not listed
          </Button>
        ) : (
          <Box sx={{ mt: 1.5, p: 1.5, border: `1px dashed ${C.border}`, borderRadius: 1.5, bgcolor: C.bg }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.navy, mb: 1 }}>
              Add a candidate not yet in the FEC field
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField size="small" placeholder="First" value={mFirst}
                onChange={e => setMFirst(e.target.value)} sx={{ ...inputSx, flex: 1 }} />
              <TextField size="small" placeholder="Last" value={mLast}
                onChange={e => setMLast(e.target.value)} sx={{ ...inputSx, flex: 1 }} />
              <FormControl size="small" sx={{ minWidth: 84 }}>
                <Select value={mParty} onChange={e => setMParty(e.target.value)} sx={selectSx}
                  MenuProps={{ disablePortal: true, disableScrollLock: true }}>
                  {PARTY_OPTIONS.map(o =>
                    <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>{o.value}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            {mErr && <Typography sx={{ fontSize: 11, color: C.red, mt: 0.75 }}>{mErr}</Typography>}
            <Box sx={{ display: "flex", gap: 1, mt: 1.25, alignItems: "center" }}>
              <Button size="small" variant="contained" onClick={addManual}
                sx={{ ...btnPrimary, py: 0.6, flex: 1 }}>
                Set as primary
              </Button>
              <Button size="small" onClick={() => setAdding(false)}
                sx={{ textTransform: "none", color: C.muted, fontSize: 12, minWidth: 0 }}>
                Cancel
              </Button>
            </Box>
            <Typography sx={{ fontSize: 10.5, color: C.muted, mt: 1, lineHeight: 1.4 }}>
              Creates your candidate for {race?.office_full || "this race"} with the FEC field as opponents.
              FEC finance links automatically once they file.
            </Typography>
          </Box>
        )
      )}
    </Box>
  );
}

// ─── Step 4: User assignment ──────────────────────────────────────────────────

function UserAssignPanel({ primaryCandidate, users, selectedUserIds, onToggleUser, onNext, onBack }) {
  return (
    <Box>
      <BackBtn onClick={onBack} label="← Change primary candidate" />
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: C.navy, mb: 0.25 }}>
        Assign users
      </Typography>
      <Typography sx={{ fontSize: 12, color: C.muted, mb: 2 }}>
        Who should have access to {fmtName(primaryCandidate?.name ?? "")}'s dashboard?
      </Typography>

      {users.length === 0
        ? <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={20} /></Box>
        : (
          <Box sx={{ maxHeight: 300, overflowY: "auto", pr: 0.5, mb: 2 }}>
            {users.map(u => (
              <Box
                key={u.id}
                component="label"
                sx={{
                  display: "flex", alignItems: "center", gap: 1.5, py: 1, px: 1.5,
                  borderRadius: 1, cursor: "pointer", mb: 0.5,
                  bgcolor: selectedUserIds.includes(u.id) ? "#eef2fa" : "transparent",
                  "&:hover": { bgcolor: "#f5f7fa" },
                }}
              >
                <Checkbox
                  size="small"
                  checked={selectedUserIds.includes(u.id)}
                  onChange={() => onToggleUser(u.id)}
                  sx={{ p: 0, color: C.navy, "&.Mui-checked": { color: C.navy } }}
                />
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.navy }}>
                    {u.first_name} {u.last_name}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: C.muted }}>{u.email}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}

      <Button fullWidth variant="contained" onClick={onNext} sx={btnPrimary}>
        Continue
      </Button>
    </Box>
  );
}

// ─── Step 5: Review & provision ───────────────────────────────────────────────

function ReviewPanel({ race, primaryCand, allCands, assignedUsers, onProvision, loading, error, onBack }) {
  // Determine which candidates are challengers (not the primary)
  const isPrimary = (c) =>
    (c.fec_id          && c.fec_id          === primaryCand?.fec_id)          ||
    (c.transparency_id && c.transparency_id === primaryCand?.transparency_id) ||
    (c.transparency_slug && c.transparency_slug === primaryCand?.transparency_slug) ||
    (!c.fec_id && !c.transparency_id && !c.transparency_slug &&
      (c.name || "").toLowerCase().trim() === (primaryCand?.name || "").toLowerCase().trim());

  const challengers = (allCands || []).filter(c => !isPrimary(c));
  const total = 1 + challengers.length;

  const InfoBox = ({ label, children }) => (
    <Box sx={{ bgcolor: C.bg, borderRadius: 1.5, p: 2, mb: 1.5 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", mb: 0.75 }}>
        {label}
      </Typography>
      {children}
    </Box>
  );

  return (
    <Box>
      <BackBtn onClick={onBack} label="← Change assignment" />
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: C.navy, mb: 2 }}>
        Review & provision
      </Typography>

      <InfoBox label="Race">
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.navy }}>
          {race?.office_full} · {race?.state} · {race?.cycle}
        </Typography>
      </InfoBox>

      <InfoBox label="Primary (your candidate)">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {primaryCand?.party && <PartyChip party={primaryCand.party} />}
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.navy }}>
            {fmtName(primaryCand?.name ?? "")}
          </Typography>
        </Box>
      </InfoBox>

      {challengers.length > 0 && (
        <InfoBox label={`Challenger campaigns (${challengers.length})`}>
          {challengers.map((c, i) => (
            <Typography key={i} sx={{ fontSize: 12, color: C.navy, mb: 0.25 }}>
              · {fmtName(c.name ?? "")} {c.party ? `(${c.party.substring(0, 3)})` : ""}
            </Typography>
          ))}
        </InfoBox>
      )}

      {assignedUsers.length > 0 && (
        <InfoBox label="Assigned users">
          {assignedUsers.map(u => (
            <Typography key={u.id} sx={{ fontSize: 12, color: C.navy, mb: 0.25 }}>
              · {u.first_name} {u.last_name}
            </Typography>
          ))}
        </InfoBox>
      )}

      {error && <Alert severity="error" sx={{ mb: 2, fontSize: 12 }}>{error}</Alert>}

      <Button fullWidth variant="contained" disabled={loading} onClick={onProvision} sx={btnPrimary}>
        {loading
          ? <CircularProgress size={18} sx={{ color: C.white }} />
          : `Provision ${total} campaign${total !== 1 ? "s" : ""}`}
      </Button>
    </Box>
  );
}

// ─── Step 6: Done ─────────────────────────────────────────────────────────────

function DonePanel({ result, onClose }) {
  const total = result?.all_campaign_ids?.length ?? 0;
  return (
    <Box sx={{ textAlign: "center", py: 1 }}>
      <Typography sx={{ fontSize: 40, mb: 1, color: "#16a34a" }}>✓</Typography>
      <Typography sx={{ fontWeight: 700, fontSize: 16, color: C.navy, mb: 0.5 }}>
        {total} campaign{total !== 1 ? "s" : ""} created
      </Typography>
      <Typography sx={{ fontSize: 12, color: C.muted, mb: 3 }}>
        Data sync is running for each campaign. The dashboard will populate within a few minutes.
      </Typography>
      <Button fullWidth variant="contained" onClick={onClose} sx={btnPrimary}>
        Done
      </Button>
    </Box>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ProvisionCampaignModal({ onClose, onProvisioned }) {
  const { authFetch } = useAuth();

  const [step,          setStep]         = useState(0);
  const [level,         setLevel]        = useState(null);
  const [filter,        setFilter]       = useState({ state: "SC", cycle: 2026, office: "H" });
  const [races,         setRaces]        = useState(null);
  const [selectedRace,  setSelectedRace] = useState(null);
  const [candidates,    setCandidates]   = useState([]);
  const [primaryCand,   setPrimaryCand]  = useState(null);
  const [allCands,      setAllCands]     = useState([]);
  const [allUsers,      setAllUsers]     = useState([]);
  const [userIds,       setUserIds]      = useState([]);
  const [result,        setResult]       = useState(null);
  const [loading,       setLoading]      = useState(false);
  const [error,         setError]        = useState("");

  // Pre-load users list so it's ready by step 4
  useEffect(() => {
    authFetch(`${PHP_API_URL}/campaigns/users-list`)
      .then(r => r.json())
      .then(d => { if (d.users) setAllUsers(d.users); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clearError = () => setError("");

  // Step 0 → 1: level chosen
  const onLevelSelect = (lvl) => {
    setLevel(lvl);
    setStep(lvl === "local" ? 11 : 1); // step 11 = local manual entry
  };

  // Step 1 → 2: fetch races
  const fetchRaces = async ({ state, cycle, office }) => {
    clearError();
    setLoading(true);
    setRaces(null);
    setFilter({ state, cycle, office });
    try {
      const qs = new URLSearchParams({ level, state, cycle, office });
      const r  = await authFetch(`${PHP_API_URL}/campaigns/discover-races?${qs}`);
      const d  = await r.json();
      if (!r.ok) { setError(d.error || "Failed to fetch races"); return; }
      setRaces(d.races || []);
      setStep(2);
    } catch {
      setError("Failed to fetch races — check your connection.");
    } finally {
      setLoading(false);
    }
  };

  // Step 2 → 3: race selected, fetch its candidates
  const selectRace = async (race) => {
    setSelectedRace(race);
    clearError();

    // Federal: candidates already embedded in the races response
    if (level === "federal" && race.candidates?.length) {
      setCandidates(race.candidates);
      setStep(3);
      return;
    }

    // State: need a separate call
    setLoading(true);
    setStep(3);
    try {
      const qs = new URLSearchParams({
        level,
        state:     filter.state,
        cycle:     filter.cycle,
        race_slug: race.race_slug ?? race.race_id ?? "",
        office:    filter.office ?? "",
        district:  race.district ?? "",
      });
      const r = await authFetch(`${PHP_API_URL}/campaigns/discover-candidates?${qs}`);
      const d = await r.json();
      if (!r.ok) { setError(d.error || "Failed to fetch candidates"); return; }
      setCandidates(d.candidates || []);
    } catch {
      setError("Failed to fetch candidates.");
    } finally {
      setLoading(false);
    }
  };

  // Step 3 → 4: primary candidate chosen — exclude withdrawn/disqualified from provisioning
  const selectPrimary = (cand, allInRace) => {
    setPrimaryCand(cand);
    setAllCands(allInRace.filter(c => c.status !== "Withdrew" && c.status !== "Disqualified"));
    setStep(4);
  };

  // Step 11 (Local): candidate entered manually → jump to step 4
  const onLocalSubmit = (cand, f) => {
    setFilter(f);
    setSelectedRace({ race_id: "local", office_full: cand.office_full, state: f.state, cycle: f.cycle });
    setPrimaryCand(cand);
    setAllCands([cand]); // single candidate for local
    setStep(4);
  };

  const toggleUser = (uid) =>
    setUserIds(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);

  // Step 5 → 6: provision all campaigns
  const provision = async () => {
    clearError();
    setLoading(true);
    try {
      const r = await authFetch(`${PHP_API_URL}/campaigns/provision-race`, {
        method: "POST",
        body: JSON.stringify({
          level,
          state:             filter.state,
          cycle:             filter.cycle,
          office:            filter.office ?? "",
          race_slug:         selectedRace?.race_slug ?? selectedRace?.race_id ?? "",
          district:          selectedRace?.district ?? "",
          office_full:       selectedRace?.office_full ?? "",
          primary_candidate: primaryCand,
          all_candidates:    allCands,
          assigned_user_ids: userIds,
          details:           {},
        }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || "Provisioning failed"); return; }

      setResult(d);

      // Trigger data-sync for every created campaign (fire and forget)
      (d.all_campaign_ids ?? []).forEach(cid => {
        authFetch(`${PHP_API_URL}/data-sync/trigger?campaign_id=${cid}`, { method: "POST" })
          .catch(() => {});
      });

      setStep(6);
      onProvisioned?.(d.primary_campaign);
    } catch {
      setError("Provisioning failed — try again.");
    } finally {
      setLoading(false);
    }
  };

  const assignedUserObjs = allUsers.filter(u => userIds.includes(u.id));

  return (
    <ModalShell onClose={onClose}>
      {step === 0  && <LevelPanel onSelect={onLevelSelect} />}

      {step === 1  && (
        <FilterPanel
          level={level}
          onSearch={fetchRaces}
          loading={loading}
          error={error}
        />
      )}

      {step === 11 && (
        <LocalPanel
          onSubmit={onLocalSubmit}
          onBack={() => setStep(0)}
        />
      )}

      {step === 2  && (
        <RaceListPanel
          races={races}
          onSelectRace={selectRace}
          onBack={() => { setStep(1); clearError(); }}
        />
      )}

      {step === 3  && (
        <CandidatePanel
          race={{ ...selectedRace, state: filter.state, cycle: filter.cycle }}
          candidates={candidates}
          loading={loading}
          error={error}
          onSelectPrimary={selectPrimary}
          onBack={() => { setStep(2); clearError(); setCandidates([]); }}
        />
      )}

      {step === 4  && (
        <UserAssignPanel
          primaryCandidate={primaryCand}
          users={allUsers}
          selectedUserIds={userIds}
          onToggleUser={toggleUser}
          onNext={() => setStep(5)}
          onBack={() => {
            if (level === "local") setStep(11);
            else setStep(3);
            clearError();
          }}
        />
      )}

      {step === 5  && (
        <ReviewPanel
          race={{ ...selectedRace, state: filter.state, cycle: filter.cycle }}
          primaryCand={primaryCand}
          allCands={allCands}
          assignedUsers={assignedUserObjs}
          onProvision={provision}
          loading={loading}
          error={error}
          onBack={() => setStep(4)}
        />
      )}

      {step === 6  && <DonePanel result={result} onClose={onClose} />}
    </ModalShell>
  );
}

// ─── Modal shell ──────────────────────────────────────────────────────────────

function ModalShell({ onClose, children }) {
  return (
    <Box
      sx={{
        position: "fixed", inset: 0, zIndex: 9999,
        bgcolor: "rgba(11,31,58,0.75)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Box
        sx={{
          bgcolor: C.white, borderRadius: 2, width: "100%", maxWidth: 520,
          mx: 2, boxShadow: "0 24px 64px rgba(0,0,0,0.3)", overflow: "hidden",
          maxHeight: "90vh", display: "flex", flexDirection: "column",
        }}
      >
        <Box sx={{
          bgcolor: C.navy, px: 3, py: 2,
          display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
        }}>
          <Typography sx={{ color: C.white, fontWeight: 700, fontSize: 15 }}>
            Provision new campaign
          </Typography>
          <Box onClick={onClose}
            sx={{ color: "#5a7898", cursor: "pointer", fontSize: 20, lineHeight: 1, "&:hover": { color: C.white } }}>
            ×
          </Box>
        </Box>
        <Box sx={{ p: 3, overflowY: "auto", flex: 1 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}
